import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { countryName } from '@/lib/countries'
import type {
    JurisdictionOverrideLiftRequest,
    JurisdictionOverrideWire,
} from '@/types/jurisdictions'
import { overrideErrorMessage } from './overrideCopy'

interface Props {
    override: JurisdictionOverrideWire | null
    onOpenChange: (open: boolean) => void
    onLift: (id: string, body: JurisdictionOverrideLiftRequest) => Promise<unknown>
}

export function LiftOverrideDialog({ override, onOpenChange, onLift }: Props) {
    const open = override !== null
    const [note, setNote] = useState('')
    const [showError, setShowError] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    const [wasId, setWasId] = useState<string | null>(null)
    const id = override?.id ?? null
    if (id !== wasId) {
        setWasId(id)
        setNote('')
        setShowError(false)
        setSubmitError(null)
    }

    const noteError = note.trim() ? null : 'Add a note saying why the block can go.'

    function handleOpenChange(next: boolean) {
        if (pending) return
        onOpenChange(next)
    }

    async function submit() {
        if (!override) return
        setShowError(true)
        setSubmitError(null)
        if (noteError) return
        setPending(true)
        try {
            await onLift(override.id, { note: note.trim() })
            onOpenChange(false)
        } catch (err) {
            setSubmitError(overrideErrorMessage(err))
        } finally {
            setPending(false)
        }
    }

    const name = override ? `${countryName(override.country_code)} (${override.country_code})` : ''

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Lift the block on {name}</DialogTitle>
                    <DialogDescription>
                        Players in {name} can register, enter and join the waitlist again within a
                        minute, unless the jurisdiction matrix blocks the country.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-1">
                    <Label htmlFor="jo-lift-note">Lift note</Label>
                    <textarea
                        id="jo-lift-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Why the block can go (review outcome, matrix updated…)."
                        className="border-input bg-background block min-h-[5rem] w-full rounded-md border p-2 text-sm"
                        aria-invalid={showError && !!noteError}
                        aria-describedby={showError && noteError ? 'jo-lift-note-error' : undefined}
                    />
                    {showError && noteError ? (
                        <p id="jo-lift-note-error" className="text-destructive text-xs">
                            {noteError}
                        </p>
                    ) : (
                        <p className="text-muted-foreground text-[11px]">
                            Stored verbatim in the audit log.
                        </p>
                    )}
                </div>

                {submitError && (
                    <p role="alert" className="text-destructive text-sm">
                        {submitError}
                    </p>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={pending}
                    >
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={pending}>
                        {pending ? 'Lifting…' : 'Lift block'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
